import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiLock, FiUnlock, FiUsers } from 'react-icons/fi';
import { cGroupInvite, cJoinGroupByCode, fullUrl } from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import { BLUE, BLUE_GRADIENT, compactNumber } from '../../components/community/brand';

/**
 * The page an **invite link** opens: `/join/<code>`.
 *
 * It shows the group (logo, name, members, public/private) and joins with one
 * tap — after signing in, because a community account is needed to be a member.
 */
export default function JoinGroupPage() {
  const { code } = useParams();
  const { t } = useLanguage();
  const { user, requireAuth, openAuth } = useCommunity();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    cGroupInvite(code)
      .then(setGroup)
      .catch((err) => setError(err?.response?.data?.detail || t('groupInviteInvalid')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const join = async () => {
    if (!requireAuth(t('loginRequiredDesc'))) return;
    setBusy(true);
    try {
      const res = await cJoinGroupByCode(code);
      toast.success(t('groupJoined'));
      navigate(`/groups/${res.group.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="mx-auto max-w-md px-4 py-12 text-center">
        <p className="text-4xl mb-2">🔗</p>
        <p className="text-sm text-gray-500">{error}</p>
        <button
          type="button"
          onClick={() => navigate('/groups')}
          className="mt-4 px-4 py-2 rounded-full text-white text-sm font-bold"
          style={{ background: BLUE }}
        >
          {t('groupsNav')}
        </button>
      </div>
    );
  }
  if (!group) {
    return <p className="mx-auto max-w-md px-4 py-12 text-center text-sm text-gray-400">{t('loading')}</p>;
  }

  return (
    <div className="mx-auto max-w-md px-4 py-8">
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden anim-pop">
        <div className="h-24" style={{ background: BLUE_GRADIENT }} />
        <div className="px-4 pb-5 -mt-10 text-center">
          {group.avatar ? (
            <img src={fullUrl(group.avatar)} alt={group.name} className="w-20 h-20 rounded-2xl object-cover mx-auto ring-4 ring-white dark:ring-gray-900" />
          ) : (
            <span
              className="w-20 h-20 rounded-2xl mx-auto flex items-center justify-center text-white text-2xl font-extrabold ring-4 ring-white dark:ring-gray-900"
              style={{ background: BLUE }}
            >
              {(group.name || 'G').slice(0, 1).toUpperCase()}
            </span>
          )}
          <h1 className="mt-3 font-extrabold text-lg flex items-center justify-center gap-2">
            {group.name}
            {group.is_public
              ? <FiUnlock className="w-4 h-4 text-emerald-600" title={t('groupPublic')} />
              : <FiLock className="w-4 h-4 text-gray-400" title={t('groupPrivate')} />}
          </h1>
          <p className="text-xs text-gray-500">
            <FiUsers className="w-3 h-3 inline" /> {compactNumber(group.member_count)} {t('groupMembers')}
          </p>
          {group.about && <p className="mt-2 text-sm text-gray-500">{group.about}</p>}

          <button
            type="button"
            disabled={busy}
            onClick={() => (user ? join() : openAuth('signin', t('loginRequiredDesc')))}
            className="mt-4 w-full py-3 rounded-2xl text-white font-bold disabled:opacity-60"
            style={{ background: BLUE }}
          >
            {group.is_member ? t('groupChat') : t('groupJoin')}
          </button>
          <p className="mt-2 text-[11px] text-gray-400">{t('groupInviteLinkHint')}</p>
        </div>
      </div>
    </div>
  );
}